import {type AlertVariants} from 'platform/components';
import {match} from 'ts-pattern';

import {isNil, isNotNil, partition, reject} from 'ramda';

import {
  type PkwEnvkvFailureReason,
  type PkwEnvkvStatusResponseBody,
  type PkwEnvkvWorkflowStatus,
} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {
  getPkwEnvkvAdvertisingCorrectionIssueCodes,
  getPkwEnvkvVehicleEditTabs,
  groupPkwEnvkvIssues,
  isPkwEnvkvApplicabilityIssue,
  type PkwEnvkvIssueGroup,
  type PkwEnvkvVehicleEditTab,
} from '@dms/shared';

import {getSafely} from 'shared/web';

export type PkwEnvkvCardAction = 'calculate' | 'generate';

export type PkwEnvkvStateAlert = {
  variant: AlertVariants;
  title: string;
  message?: string;
};

export type PkwEnvkvReadinessAlert = PkwEnvkvStateAlert & {
  // One line per correction, in the order the corrections should be made.
  issues: string[];
  // The edit tabs behind the issue lines, in the same order.
  vehicleEditTabs: PkwEnvkvVehicleEditTab[];
};

const PKW_ENVKV_VEHICLE_EDIT_TAB_LABEL_KEYS: Readonly<Record<PkwEnvkvVehicleEditTab, string>> = {
  'basic-information': 'general.labels.basicInformation',
  'vehicle-data': 'entity.vehicle.labels.vehicleData',
};

export type PkwEnvkvState = {
  isVisible: boolean;
  eligibilityAlert: PkwEnvkvStateAlert | undefined;
  readinessAlert: PkwEnvkvReadinessAlert | undefined;
  statusAlert: PkwEnvkvStateAlert | undefined;
  actions: PkwEnvkvCardAction[];
  // Offered on the in-progress alert once the running attempt is old enough to be
  // retried on the backend; the header actions stay empty while an attempt runs.
  retryAction?: PkwEnvkvCardAction | undefined;
  previewFileId: string | null;
  areCalculationRowsVisible: boolean;
};

type WorkflowFacts = {
  actions: PkwEnvkvCardAction[];
  statusAlert: PkwEnvkvStateAlert | undefined;
  isDocumentCurrent: boolean;
};

type ServerInstant = string | null | undefined;

// The backend accepts a retry of a running attempt once it is at least this old.
const RETRY_AFTER_MICROSECONDS = 30_000_000;
const MICROSECONDS_PER_MILLISECOND = 1000;
const FRACTION_DIGITS = 6;
const MILLISECOND_DIGITS = 3;
// An RFC 3339 instant is checked in three fixed pieces instead of one pattern with an
// optional quantified group: "2026-09-15T10:00:00", the digits after the decimal point
// (the backend sends six) and the offset ("Z" or "+00:00").
const RFC3339_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/;
const RFC3339_FRACTION = /^\d{1,9}$/;
const RFC3339_NUMERIC_OFFSET = /^[+-]\d{2}:\d{2}$/;
const UTC_OFFSET = 'Z';
const NUMERIC_OFFSET_LENGTH = 6;

/**
 * Lists the issues grouped by the correction that fixes them: the vehicle
 * fields of one edit tab on a single line behind the tab name, then each
 * calculated value and remaining issue on its own line. The tab holding an
 * applicability input comes first, and inside a tab its fields lead.
 */
function getReadinessIssues(
  codes: string[]
): Pick<PkwEnvkvReadinessAlert, 'issues' | 'vehicleEditTabs'> {
  const applicabilityTabs = getPkwEnvkvVehicleEditTabs(codes.filter(isPkwEnvkvApplicabilityIssue));
  const isApplicabilityGroup = (group: PkwEnvkvIssueGroup) =>
    group.kind === 'vehicle-fields' && applicabilityTabs.includes(group.tab);
  const [applicabilityGroups, otherGroups] = partition(
    isApplicabilityGroup,
    groupPkwEnvkvIssues([
      ...codes.filter(isPkwEnvkvApplicabilityIssue),
      ...reject(isPkwEnvkvApplicabilityIssue, codes),
    ])
  );
  const groups = [...applicabilityGroups, ...otherGroups];

  return {
    issues: groups.flatMap((group) =>
      group.kind === 'vehicle-fields'
        ? [
            `${i18n.t(PKW_ENVKV_VEHICLE_EDIT_TAB_LABEL_KEYS[group.tab])}: ${group.labels.join(', ')}`,
          ]
        : group.labels
    ),
    vehicleEditTabs: groups.flatMap((group) =>
      group.kind === 'vehicle-fields' ? [group.tab] : []
    ),
  };
}

const FAILURE_REASON_MESSAGE_KEYS: Readonly<Record<PkwEnvkvFailureReason, string>> = {
  CALCULATION_DATA_UNAVAILABLE: 'page.pkwEnvkv.failureReasons.calculationDataUnavailable',
  NOT_CONFIGURED: 'page.pkwEnvkv.failureReasons.notConfigured',
  UNSUPPORTED_INPUT: 'page.pkwEnvkv.failureReasons.unsupportedInput',
  DOCUMENT_SERVICE_UNAVAILABLE: 'page.pkwEnvkv.failureReasons.documentServiceUnavailable',
  DOCUMENT_STORAGE_FAILED: 'page.pkwEnvkv.failureReasons.documentStorageFailed',
  UNKNOWN: 'page.pkwEnvkv.failureReasons.unknown',
};

// The status is not parsed outside development, so a reason added on the
// backend later still gets the generic text instead of a missing translation.
const getFailureReasonMessage = (reason: PkwEnvkvStatusResponseBody['failureReason']) =>
  isNil(reason)
    ? undefined
    : i18n.t(getSafely(FAILURE_REASON_MESSAGE_KEYS, reason, FAILURE_REASON_MESSAGE_KEYS.UNKNOWN));

/**
 * Integer microseconds since the epoch, or undefined for a missing or invalid
 * instant. Date.parse keeps milliseconds only; the backend serializes six
 * fractional digits and decides the retry boundary on them, so the
 * sub-millisecond digits are re-attached here instead of being dropped.
 */
function parseServerInstantMicroseconds(instant: ServerInstant): number | undefined {
  if (isNil(instant)) {
    return undefined;
  }
  const offset = instant.endsWith(UTC_OFFSET) ? UTC_OFFSET : instant.slice(-NUMERIC_OFFSET_LENGTH);
  const localTime = instant.slice(0, instant.length - offset.length);
  const decimalPoint = localTime.indexOf('.');
  const dateTime = decimalPoint === -1 ? localTime : localTime.slice(0, decimalPoint);
  const fractionDigits = decimalPoint === -1 ? '' : localTime.slice(decimalPoint + 1);
  const isWellFormed =
    (offset === UTC_OFFSET || RFC3339_NUMERIC_OFFSET.test(offset)) &&
    RFC3339_DATE_TIME.test(dateTime) &&
    (decimalPoint === -1 || RFC3339_FRACTION.test(fractionDigits));
  if (!isWellFormed) {
    return undefined;
  }
  const fraction = fractionDigits.padEnd(FRACTION_DIGITS, '0').slice(0, FRACTION_DIGITS);
  const microseconds =
    Date.parse(`${dateTime}.${fraction.slice(0, MILLISECOND_DIGITS)}${offset}`) * // eslint-disable-line eag/no-native-date-math -- Pre-existing violation
      MICROSECONDS_PER_MILLISECOND +
    Number(fraction.slice(MILLISECOND_DIGITS));

  return Number.isSafeInteger(microseconds) ? microseconds : undefined;
}

/**
 * Elapsed time of a running attempt on the server clock: the backend's
 * evaluation instant minus the attempt's own start. The browser clock never
 * takes part, so a skewed client cannot offer a retry the backend refuses.
 */
function isRetryDue(evaluatedAt: ServerInstant, startedAt: ServerInstant): boolean {
  const evaluated = parseServerInstantMicroseconds(evaluatedAt);
  const started = parseServerInstantMicroseconds(startedAt);
  if (isNil(evaluated) || isNil(started)) {
    return false;
  }

  return evaluated - started >= RETRY_AFTER_MICROSECONDS;
}

/**
 * Derives the whole card view model from one backend status. The workflow
 * match is exhaustive on purpose: a new backend workflow status must fail to
 * compile here instead of silently rendering a card with no actions.
 */
export function derivePkwEnvkvState(
  status: PkwEnvkvStatusResponseBody,
  options: {canGenerateDocument: boolean}
): PkwEnvkvState {
  const advertisingCorrectionIssueCodes = getPkwEnvkvAdvertisingCorrectionIssueCodes(status);
  // For a vehicle that requires EnVKV the backend sends an advertising status,
  // and the readiness alert replaces the eligibility alert; the eligibility
  // alert stays only for vehicles where EnVKV is not (or not yet) required.
  const readinessAlert = match<
    PkwEnvkvStatusResponseBody['advertisingStatus'],
    PkwEnvkvReadinessAlert | undefined
  >(status.advertisingStatus)
    .with(null, () => undefined)
    .with('GREEN', () => ({
      variant: 'success' as const,
      title: i18n.t('page.pkwEnvkv.advertising.ready'),
      issues: [],
      vehicleEditTabs: [],
    }))
    .with('YELLOW', () => ({
      variant: 'warning' as const,
      title: i18n.t('page.pkwEnvkv.advertising.incomplete'),
      ...getReadinessIssues(status.issues),
    }))
    .with('RED', () => ({
      variant: 'error' as const,
      title: i18n.t('page.pkwEnvkv.advertising.blocked'),
      ...getReadinessIssues(advertisingCorrectionIssueCodes),
    }))
    .exhaustive();

  const eligibilityAlert = match<
    PkwEnvkvStatusResponseBody['legalApplicabilityStatus'],
    PkwEnvkvStateAlert
  >(status.legalApplicabilityStatus)
    .with('APPLICABLE', () => ({
      variant: 'success',
      title: i18n.t('page.pkwEnvkv.eligibility.eligible'),
    }))
    .with('NOT_APPLICABLE', () => ({
      variant: 'warning',
      title: i18n.t('page.pkwEnvkv.eligibility.notEligible'),
    }))
    .with('UNKNOWN', () => ({
      variant: 'info',
      title: i18n.t('page.pkwEnvkv.eligibility.unknown'),
    }))
    .exhaustive();

  const workflow = match<PkwEnvkvWorkflowStatus, WorkflowFacts>(status.workflowStatus)
    .with('UNSUPPORTED_COUNTRY', 'NOT_APPLICABLE', 'APPLICABILITY_UNKNOWN', () => ({
      actions: [],
      statusAlert: undefined,
      isDocumentCurrent: false,
    }))
    .with('MISSING_DATA', () => ({
      actions: [],
      statusAlert: {
        variant: 'warning',
        title: i18n.t('page.pkwEnvkv.status.missingData'),
      },
      isDocumentCurrent: false,
    }))
    .with('READY_TO_CALCULATE', 'OUTDATED', () => ({
      actions: ['calculate'],
      statusAlert: undefined,
      isDocumentCurrent: false,
    }))
    .with('CALCULATING', () => ({
      actions: [],
      statusAlert: {
        variant: 'info',
        title: i18n.t('page.pkwEnvkv.status.calculating'),
      },
      isDocumentCurrent: false,
    }))
    .with('CALCULATION_FAILED', () => ({
      actions: ['calculate'],
      statusAlert: {
        variant: 'error',
        title: i18n.t('page.pkwEnvkv.status.calculationFailed'),
        message: getFailureReasonMessage(status.failureReason),
      },
      isDocumentCurrent: false,
    }))
    .with('READY_TO_GENERATE', () => ({
      actions: ['generate'],
      statusAlert: undefined,
      isDocumentCurrent: false,
    }))
    .with('GENERATING', () => ({
      actions: [],
      statusAlert: {
        variant: 'info',
        title: i18n.t('page.pkwEnvkv.status.generating'),
      },
      isDocumentCurrent: false,
    }))
    // The backend rejects a calculation from READY_TO_GENERATE,
    // GENERATION_FAILED and CURRENT, and a generation from CURRENT: a current
    // document keeps its file link until the vehicle data change (OUTDATED).
    .with('CURRENT', () => ({
      actions: [],
      statusAlert: undefined,
      isDocumentCurrent: true,
    }))
    .with('GENERATION_FAILED', () => ({
      actions: ['generate'],
      statusAlert: {
        variant: 'error',
        title: i18n.t('page.pkwEnvkv.status.generationFailed'),
        message: getFailureReasonMessage(status.failureReason),
      },
      isDocumentCurrent: false,
    }))
    .exhaustive();

  const retryAction = match<PkwEnvkvWorkflowStatus, PkwEnvkvCardAction | undefined>(
    status.workflowStatus
  )
    .with('CALCULATING', () =>
      isRetryDue(status.evaluatedAt, status.calculationStartedAt) ? 'calculate' : undefined
    )
    .with('GENERATING', () =>
      isRetryDue(status.evaluatedAt, status.documentStartedAt) ? 'generate' : undefined
    )
    .otherwise(() => undefined);

  // The readiness alert already lists the missing data, so the generic
  // missing-data alert would only duplicate it. Lifecycle alerts (calculating,
  // generating, failures) and the fallback without an advertising status stay.
  const isDuplicateMissingDataAlert =
    isNotNil(readinessAlert) && status.workflowStatus === 'MISSING_DATA';

  return {
    isVisible: status.supported,
    eligibilityAlert: isNotNil(readinessAlert) ? undefined : eligibilityAlert,
    readinessAlert,
    statusAlert: isDuplicateMissingDataAlert ? undefined : workflow.statusAlert,
    actions: options.canGenerateDocument ? workflow.actions : [],
    retryAction: options.canGenerateDocument ? retryAction : undefined,
    previewFileId:
      options.canGenerateDocument && workflow.isDocumentCurrent ? status.documentFileId : null,
    areCalculationRowsVisible: status.legalApplicabilityStatus !== 'NOT_APPLICABLE',
  };
}
